import type { Database } from '../database.types';
import { getSupabaseClient, type DdmSupabaseClient } from '../supabase';
import { escapeIlikeTerm } from './ilike';

export type Deal = Database['public']['Tables']['deals']['Row'];
export type DealPhoto = Database['public']['Tables']['deal_photos']['Row'];

export type DealWithPhotos = Deal & {
  deal_photos: Pick<DealPhoto, 'url' | 'alt_text' | 'position'>[];
};

/** Catalogue / detail status pill kind, derived from `deals.type` + `status`. */
export type DealKind = 'READY' | 'OFF-PLAN' | 'SOLD';

/**
 * UI view-model for a deal, produced by `mapDealRowToDealView` from a real
 * `DealWithPhotos` row. camelCase, derived numerics, and the persisted DLD
 * snapshot — the shape consumed by the catalogue card, detail page, and
 * benchmark panel. (Formerly `DealMock`; no longer a fixture type.)
 */
export type DealView = {
  id: string;
  // Human-readable reference (`DDM-YYYY-NNNNNN`), generated by the
  // `reference_id` Postgres trigger. Used everywhere the UI surfaces the deal
  // identifier; `id` (UUID) stays internal to React keys and routing.
  referenceId: string;
  slug: string;
  community: string;
  zone: string;
  buildingTitle: string;
  /** DLD building label (deals.building) — named in the BenchmarkPanel building
   *  section (ADR-047). Distinct from `buildingTitle` (the editorial deal title).
   *  NULL when the deal is not mapped to a specific building. */
  building?: string | null;
  bedrooms: number;
  bathrooms: number;
  areaSqft: number;
  askingAed: number;
  dldReferenceAed: number;
  discountPct: number; // positive percentage saved vs reference_price, e.g. 32
  /**
   * Scope-aware DLD confidence (ADR-047), persisted on `deals.dld_confidence`.
   * Drives the discount-wording gating via `discountDisplayMode`. Optional so
   * the view-model stays flexible; real rows via `mapDealRowToDealView` always
   * set it (NULL only before the first recalc).
   */
  dldConfidence?: 'high' | 'medium' | 'low' | 'insufficient' | 'off_plan_reference' | null;
  /** Persisted DLD scope snapshot (`deals.dld_scope`), e.g. `same_building_3m`. */
  dldScope?: string | null;
  pricePerSqftAed: number;
  benchmarkSqftAed: number;
  status: DealKind;
  /**
   * Raw deal type (`deals.type`) — the RELIABLE off-plan/ready signal that
   * drives the price-reference wording (off-plan → "Original price", ready →
   * "DLD ref"). Distinct from `status` (DealKind), which collapses both into
   * `'SOLD'` once sold, and from `discountDisplayMode` (gates the % badge only,
   * not the always-rendered reference line). Optional so the view-model stays
   * flexible; real rows via the mappers always set it.
   */
  dealType?: 'ready' | 'off_plan' | null;
  imageLabel: string;
  /** Assigned advisor FK (`deals.advisor_id`), NULL when none is assigned.
   *  Feeds the card WhatsApp cascade (assigned → default) via
   *  `useAdvisorDirectory` / `pickAdvisorWhatsAppPhone`. */
  advisorId?: string | null;
  isFeatured: boolean;
  isPick: boolean;
  floor?: string;
  view?: string;
  handover?: string;
  /**
   * Property sub-type (ADR-027): 'apartment' | 'villa' | 'townhouse' |
   * 'penthouse' | 'plot' | 'commercial'. Optional; real rows via
   * `mapDealRowToDealView` always set it.
   */
  propertyType?: string;
  publishedAt: string;
  description: string;
  // BO-4 — optional fields populated by `mapDealRowToDealView` from real
  // `deal_photos` rows. Fall back to <ImagePlaceholder> when undefined.
  imageUrl?: string;
  photos?: Array<{ url: string; alt_text: string; position: number }>;
  latitude?: number;
  longitude?: number;
};

// PostgREST embed string shared by the catalogue (`searchPublishedDeals`)
// and the deal detail (`getDealBySlug`). LEFT join — deals without a cover
// photo must still be returned so the catalogue / detail can fall back to
// `<ImagePlaceholder>` (Q4 arbitrage 2026-05-02).
const SELECT_WITH_PHOTOS = '*, deal_photos(url, alt_text, position)';

export async function listPublishedDeals(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<Deal[]> {
  const { data, error } = await client
    .from('deals')
    .select('*')
    .eq('status', 'published')
    .is('deleted_at', null)
    .order('publication_date', { ascending: false, nullsFirst: false });
  if (error) throw error;
  return data ?? [];
}

// Public deal detail by slug — used by `/deals/:slug` (M4 / BO-4). Anon RLS
// `deals_public_select` already restricts visibility to (published, sold)
// AND deleted_at IS NULL; we apply the same filter explicitly to keep the
// EXPLAIN plan aligned with `deals_public_catalogue_idx` and to make intent
// readable. Returns null when the slug does not match a publicly visible
// deal — the caller routes to /404 (Matrice §M4 L249: anon must not see
// draft / validating / archived / refused / expired).
//
// Photos are joined via the same LEFT embed used by the catalogue
// (`SELECT_WITH_PHOTOS`); the gallery component sorts by `position` so the
// SQL ordering is incidental. `maybeSingle()` returns null on zero rows
// rather than throwing PGRST116 — the slug-not-found case is expected, not
// an error.
export async function getDealBySlug(
  slug: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<DealWithPhotos | null> {
  const { data, error } = await client
    .from('deals')
    .select(SELECT_WITH_PHOTOS)
    .eq('slug', slug)
    .in('status', ['published', 'sold'])
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw error;
  return (data as DealWithPhotos | null) ?? null;
}

// Home — Featured + Deal of the Day (M2). Spec V1.1 §M2 (P3): no automatic
// fallback. When zero deals match, the home renders the UB15 empty state
// instead of falling back to "most recent published". The applicative
// `status='published'` filter is strictly tighter than the anon RLS policy
// (which also allows 'sold' for the 30-day SOLD badge) — sold deals are
// never featured on the home. Both queries use the partial index
// `deals_featured_idx` (migration 003 L238).
export async function getFeaturedDeals(
  limit: number = 3,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<DealWithPhotos[]> {
  const { data, error } = await client
    .from('deals')
    .select(SELECT_WITH_PHOTOS)
    .eq('status', 'published')
    .eq('is_featured', true)
    .is('deleted_at', null)
    .order('featured_at', { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as DealWithPhotos[];
}

export async function getDealOfTheDay(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<DealWithPhotos | null> {
  const { data, error } = await client
    .from('deals')
    .select(SELECT_WITH_PHOTOS)
    .eq('status', 'published')
    .eq('is_featured', true)
    .is('deleted_at', null)
    .order('featured_at', { ascending: false, nullsFirst: false })
    .limit(1);
  if (error) throw error;
  const rows = (data ?? []) as DealWithPhotos[];
  return rows[0] ?? null;
}

export type CatalogueTypeFilter = 'all' | 'ready' | 'offPlan';
export type CatalogueSort = 'discount' | 'priceAsc' | 'priceDesc' | 'dateDesc';

// ADR-027 — property_type whitelist mirrors the DB CHECK
// (deals_property_type_check). Source of truth for the front: the catalogue
// filter chips, the admin BO-3 form, the admin BO-1 list filter, and the
// CSV import Zod schema all import this list.
export const PROPERTY_TYPES = [
  'apartment',
  'villa',
  'townhouse',
  'penthouse',
  'plot',
  'commercial',
] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];

export type SearchPublishedDealsParams = {
  typeFilter: CatalogueTypeFilter;
  priceMin: number;
  priceMax: number;
  areaMin: number;
  areaMax: number;
  /** Canonical DLD zone keys to keep (ADR-045 D1). Empty = no restriction. */
  zoneKey?: string[];
  /** Canonical DLD community keys to keep. Empty = no restriction. */
  communityKey?: string[];
  /** Canonical DLD building keys to keep. Empty = no restriction. */
  buildingKey?: string[];
  /**
   * Free-text location term (addendum ADR-045 §8). Matched case-insensitively
   * against the deal TEXT columns `zone` / `community` / `building`, so deals
   * with NULL `*_key` stay discoverable. Set by a location pick or free-text
   * submit; mutually exclusive with the canonical `*_key` filters above.
   */
  locationQuery?: string;
  /**
   * Minimum absolute discount, expressed positively (e.g. 20 means "at least
   * 20% off the DLD reference"). `discount_pct` is stored as a negative
   * percentage, so the SQL filter is `discount_pct <= -discountMin`.
   */
  discountMin?: number;
  /** Bedrooms whitelist (0..10). Empty array = no bedroom restriction. */
  bedrooms?: number[];
  /**
   * Property-type whitelist (ADR-027). Empty array = no restriction.
   * Mirrors the DB CHECK on `deals.property_type`.
   */
  propertyType?: PropertyType[];
  /** Free-text keyword on title + building + description (case-insensitive
   * substring). Distinct from `locationQuery` which targets the location
   * columns. */
  searchQuery?: string;
  sortBy: CatalogueSort;
  /** 0-based page index — offset pagination (UI_Behavior §4.10 + UB7). */
  page: number;
  pageSize: number;
};

export type SearchPublishedDealsResult = {
  rows: DealWithPhotos[];
  /** Exact count of matching rows, same request (`count: 'exact'`). */
  total: number;
};

type SortColumn = 'asking_price' | 'discount_pct' | 'publication_date';
type SortDescriptor = { column: SortColumn; ascending: boolean };

function sortDescriptor(sortBy: CatalogueSort): SortDescriptor {
  if (sortBy === 'priceAsc') return { column: 'asking_price', ascending: true };
  if (sortBy === 'priceDesc') return { column: 'asking_price', ascending: false };
  if (sortBy === 'dateDesc') return { column: 'publication_date', ascending: false };
  // discount_pct stored as positive percentage saved vs reference_price
  // (e.g. 32 = 32% off). Highest first → ascending: false.
  return { column: 'discount_pct', ascending: false };
}

export async function searchPublishedDeals(
  params: SearchPublishedDealsParams,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<SearchPublishedDealsResult> {
  const sort = sortDescriptor(params.sortBy);

  let q = client
    .from('deals')
    // count: 'exact' rides the same request — rows + total stay atomically
    // consistent, which the numbered pagination needs for "Page X of Y"
    // (UI_Behavior §4.10 L665-675).
    .select(SELECT_WITH_PHOTOS, { count: 'exact' })
    // Anon RLS already filters to (published, sold) AND deleted_at IS NULL;
    // applying the same filter explicitly keeps EXPLAIN plans aligned with
    // the deals_public_catalogue_idx partial index (migration 003 L243-245).
    .in('status', ['published', 'sold'])
    .is('deleted_at', null)
    .gte('asking_price', params.priceMin)
    .lte('asking_price', params.priceMax)
    .gte('area_sqft', params.areaMin)
    .lte('area_sqft', params.areaMax);

  if (params.typeFilter === 'ready') {
    q = q.eq('type', 'ready');
  } else if (params.typeFilter === 'offPlan') {
    q = q.eq('type', 'off_plan');
  }

  // L1 filters on the canonical *_key columns (ADR-045 D1) — stable vs label
  // case/variants, backed by the deals_*_key_idx partial indices (ADR-043).
  if (params.zoneKey && params.zoneKey.length > 0) {
    q = q.in('zone_key', params.zoneKey);
  }
  if (params.communityKey && params.communityKey.length > 0) {
    q = q.in('community_key', params.communityKey);
  }
  if (params.buildingKey && params.buildingKey.length > 0) {
    q = q.in('building_key', params.buildingKey);
  }

  if (params.bedrooms && params.bedrooms.length > 0) {
    q = q.in('bedrooms', params.bedrooms);
  }

  if (params.propertyType && params.propertyType.length > 0) {
    q = q.in('property_type', params.propertyType);
  }

  if (typeof params.discountMin === 'number' && params.discountMin > 0) {
    // discount_pct stored as positive percentage saved vs reference_price.
    // "Min discount 20" selects rows whose discount is at least that strong,
    // i.e. discount_pct >= 20.
    q = q.gte('discount_pct', params.discountMin);
  }

  // Location free-text (addendum ADR-045 §8): match the deal TEXT columns so
  // deals whose *_key are NULL (off-plan / new communities absent from the DLD
  // referential) are discoverable. The legacy `.in('*_key')` filters above stay
  // for canonical `?zone_key=` external links; a UI pick clears those keys and
  // sets `locationQuery` instead, so the two paths never AND each other.
  if (params.locationQuery && params.locationQuery.trim().length > 0) {
    const lt = escapeIlikeTerm(params.locationQuery.trim());
    if (lt.length > 0) {
      q = q.or(`zone.ilike.%${lt}%,community.ilike.%${lt}%,building.ilike.%${lt}%`);
    }
  }

  if (params.searchQuery && params.searchQuery.trim().length > 0) {
    const term = escapeIlikeTerm(params.searchQuery.trim());
    if (term.length > 0) {
      q = q.or(`title.ilike.%${term}%,building.ilike.%${term}%,description.ilike.%${term}%`);
    }
  }

  if (params.sortBy === 'dateDesc') {
    // publication_date is NULL on draft and only filled at status='published'.
    // For the dateDesc sort we want a deterministic ordering, so exclude rows
    // that would land at the end of the result purely on NULL placement.
    q = q.not('publication_date', 'is', null);
  }
  // sortBy='discount': do NOT filter out rows where discount_pct IS NULL.
  // discount_pct is only auto-computed for off-plan deals (from original_price)
  // or for ready deals whose community_key matches DLD referential (legacy
  // recalc_deal_dld trigger). Ready deals with unresolved community_key (admin
  // free-text like "JVC" → no match in dld_communities) keep discount_pct NULL
  // by design; excluding them from sort=discount makes them invisible across
  // the whole catalogue view, which broke discoverability (Hani 2026-05-23).
  // ORDER BY discount_pct DESC NULLS LAST below sends them to the bottom.

  // Offset pagination — the catalogue stays small (< 1k deals at launch),
  // so OFFSET cost is a non-issue and every page number becomes directly
  // addressable (jump + `?page=` deep linking, UI_Behavior §4.10 L675).
  // The `id` tie-break keeps page boundaries deterministic when the sort
  // column has duplicates. An offset beyond the result set returns an
  // empty page with the count intact — the caller clamps.
  const from = Math.max(0, params.page) * params.pageSize;
  const { data, error, count } = await q
    .order(sort.column, { ascending: sort.ascending, nullsFirst: false })
    .order('id', { ascending: sort.ascending })
    .range(from, from + params.pageSize - 1);

  if (error) throw error;

  return { rows: (data ?? []) as DealWithPhotos[], total: count ?? 0 };
}

// ─────────────────────────────────────────────────────────────────────────
// Catalogue auxiliaries — autocomplete + header stats
// ─────────────────────────────────────────────────────────────────────────

export type CatalogueAutocompleteItem = {
  title: string;
  building: string | null;
};

const AUTOCOMPLETE_DEFAULT_LIMIT = 8;
const AUTOCOMPLETE_MIN_QUERY_LENGTH = 2;

/**
 * Search published / sold deals for an autocomplete dropdown. Returns up to
 * `limit` matches on either `title` or `building` (case-insensitive
 * substring). `title` benefits from `deals_title_trgm_idx` (migration 003
 * L258); `building` is a sequential scan, acceptable at V1 volumes.
 *
 * Queries shorter than `AUTOCOMPLETE_MIN_QUERY_LENGTH` return an empty
 * result without hitting the DB — wildcard searches on a single character
 * blow past the index and have no useful UX value.
 */
export async function getCatalogueAutocomplete(
  query: string,
  limit: number = AUTOCOMPLETE_DEFAULT_LIMIT,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<CatalogueAutocompleteItem[]> {
  const term = escapeIlikeTerm(query.trim());
  if (term.length < AUTOCOMPLETE_MIN_QUERY_LENGTH) return [];
  const { data, error } = await client
    .from('deals')
    .select('title, building')
    .in('status', ['published', 'sold'])
    .is('deleted_at', null)
    .or(`title.ilike.%${term}%,building.ilike.%${term}%`)
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as CatalogueAutocompleteItem[];
}

/**
 * Catalogue location suggestion (addendum ADR-045 §8) — a DISTINCT location
 * TEXT value from the published/sold catalogue, its level, and a popularity
 * counter. Sourced from the deal text (not the DLD referential), so deals with
 * NULL `*_key` are represented. Replaces the key-based `CatalogueLocationOption`
 * + the 3 `catalogue_available_*` facet RPCs (search text-first).
 */
export type LocationKind = 'zone' | 'community' | 'building';

export type CatalogueLocationSuggestion = {
  /** Location text exactly as stored on the deal (zone / community / building). */
  value: string;
  kind: LocationKind;
  dealCount: number;
};

type LocationCorpusRow = { value: string; kind: string; deal_count: number };

/**
 * Full location autocomplete corpus for the catalogue, via the
 * `catalogue_location_corpus` RPC. Server-ordered Building-first then by
 * popularity. The corpus is tiny (one row per DISTINCT location text), so it is
 * fetched once and filtered client-side by the location field.
 */
export async function catalogueLocationCorpus(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<CatalogueLocationSuggestion[]> {
  const { data, error } = await client.rpc('catalogue_location_corpus');
  if (error) throw error;
  return ((data ?? []) as LocationCorpusRow[]).map((r) => ({
    value: r.value,
    kind: r.kind as LocationKind,
    dealCount: r.deal_count,
  }));
}

export type CatalogueStatsFilters = Pick<
  SearchPublishedDealsParams,
  | 'typeFilter'
  | 'priceMin'
  | 'priceMax'
  | 'areaMin'
  | 'areaMax'
  | 'zoneKey'
  | 'communityKey'
  | 'buildingKey'
  | 'locationQuery'
  | 'discountMin'
  | 'bedrooms'
  | 'propertyType'
  | 'searchQuery'
>;

export type CatalogueStats = {
  count: number;
  averageDiscountPct: number | null;
  lastUpdatedAt: string | null;
};

const STATS_SAMPLE_LIMIT = 1500;

/**
 * Aggregate stats for the catalogue header — total matching count, average
 * discount, and most recent update timestamp. Two parallel queries:
 *
 *   1. Exact count via PostgREST head request (fast, index-only).
 *   2. Sample of `discount_pct` + `updated_at` for client-side aggregation.
 *
 * The sample is capped at 1500 rows — enough headroom for the V1 catalogue
 * (< 1k deals expected at launch) while bounding the payload for headers
 * that expect a snappy response. When the catalogue grows past that cap a
 * dedicated SQL aggregation RPC + 60s server cache (per Spec §M3) becomes
 * the right move.
 */
export async function getCatalogueStats(
  filters: CatalogueStatsFilters,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<CatalogueStats> {
  // Two parallel queries built independently because PostgREST types each
  // builder by its select-clause shape — a generic `applyFilters(builder)`
  // would need an `any`/cast at every chain step. Inlining is shorter than
  // the alternative.
  const buildCount = () => {
    let q = client
      .from('deals')
      .select('*', { count: 'exact', head: true })
      .in('status', ['published', 'sold'])
      .is('deleted_at', null)
      .gte('asking_price', filters.priceMin)
      .lte('asking_price', filters.priceMax)
      .gte('area_sqft', filters.areaMin)
      .lte('area_sqft', filters.areaMax);
    if (filters.typeFilter === 'ready') q = q.eq('type', 'ready');
    else if (filters.typeFilter === 'offPlan') q = q.eq('type', 'off_plan');
    if (filters.zoneKey && filters.zoneKey.length > 0) q = q.in('zone_key', filters.zoneKey);
    if (filters.communityKey && filters.communityKey.length > 0)
      q = q.in('community_key', filters.communityKey);
    if (filters.buildingKey && filters.buildingKey.length > 0)
      q = q.in('building_key', filters.buildingKey);
    if (filters.bedrooms && filters.bedrooms.length > 0) q = q.in('bedrooms', filters.bedrooms);
    if (filters.propertyType && filters.propertyType.length > 0) {
      q = q.in('property_type', filters.propertyType);
    }
    if (typeof filters.discountMin === 'number' && filters.discountMin > 0) {
      q = q.gte('discount_pct', filters.discountMin);
    }
    if (filters.locationQuery && filters.locationQuery.trim().length > 0) {
      const lt = escapeIlikeTerm(filters.locationQuery.trim());
      if (lt.length > 0)
        q = q.or(`zone.ilike.%${lt}%,community.ilike.%${lt}%,building.ilike.%${lt}%`);
    }
    if (filters.searchQuery && filters.searchQuery.trim().length > 0) {
      const term = escapeIlikeTerm(filters.searchQuery.trim());
      if (term.length > 0)
        q = q.or(`title.ilike.%${term}%,building.ilike.%${term}%,description.ilike.%${term}%`);
    }
    return q;
  };

  const buildSample = () => {
    let q = client
      .from('deals')
      .select('discount_pct, updated_at')
      .in('status', ['published', 'sold'])
      .is('deleted_at', null)
      .gte('asking_price', filters.priceMin)
      .lte('asking_price', filters.priceMax)
      .gte('area_sqft', filters.areaMin)
      .lte('area_sqft', filters.areaMax);
    if (filters.typeFilter === 'ready') q = q.eq('type', 'ready');
    else if (filters.typeFilter === 'offPlan') q = q.eq('type', 'off_plan');
    if (filters.zoneKey && filters.zoneKey.length > 0) q = q.in('zone_key', filters.zoneKey);
    if (filters.communityKey && filters.communityKey.length > 0)
      q = q.in('community_key', filters.communityKey);
    if (filters.buildingKey && filters.buildingKey.length > 0)
      q = q.in('building_key', filters.buildingKey);
    if (filters.bedrooms && filters.bedrooms.length > 0) q = q.in('bedrooms', filters.bedrooms);
    if (filters.propertyType && filters.propertyType.length > 0) {
      q = q.in('property_type', filters.propertyType);
    }
    if (typeof filters.discountMin === 'number' && filters.discountMin > 0) {
      q = q.gte('discount_pct', filters.discountMin);
    }
    if (filters.locationQuery && filters.locationQuery.trim().length > 0) {
      const lt = escapeIlikeTerm(filters.locationQuery.trim());
      if (lt.length > 0)
        q = q.or(`zone.ilike.%${lt}%,community.ilike.%${lt}%,building.ilike.%${lt}%`);
    }
    if (filters.searchQuery && filters.searchQuery.trim().length > 0) {
      const term = escapeIlikeTerm(filters.searchQuery.trim());
      if (term.length > 0)
        q = q.or(`title.ilike.%${term}%,building.ilike.%${term}%,description.ilike.%${term}%`);
    }
    return q.limit(STATS_SAMPLE_LIMIT);
  };

  const [countResult, sampleResult] = await Promise.all([buildCount(), buildSample()]);

  if (countResult.error) throw countResult.error;
  if (sampleResult.error) throw sampleResult.error;

  const count = countResult.count ?? 0;
  type StatsSampleRow = { discount_pct: number | null; updated_at: string | null };
  const sample = (sampleResult.data ?? []) as StatsSampleRow[];

  let discountSum = 0;
  let discountCount = 0;
  let lastUpdated: string | null = null;
  for (const row of sample) {
    if (row.discount_pct !== null) {
      discountSum += row.discount_pct;
      discountCount += 1;
    }
    if (row.updated_at !== null && (lastUpdated === null || row.updated_at > lastUpdated)) {
      lastUpdated = row.updated_at;
    }
  }

  return {
    count,
    averageDiscountPct: discountCount > 0 ? discountSum / discountCount : null,
    lastUpdatedAt: lastUpdated,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Admin back-office list — all-status, RLS-gated
// ─────────────────────────────────────────────────────────────────────────

export type AdminDealStatus =
  | 'draft'
  | 'validating'
  | 'published'
  | 'refused'
  | 'sold'
  | 'archived'
  | 'expired';

export type AdminDealRow = Pick<
  Deal,
  | 'id'
  | 'reference_id'
  | 'title'
  | 'building'
  | 'type'
  | 'status'
  | 'zone'
  | 'asking_price'
  | 'discount_pct'
  | 'bedrooms'
  | 'area_sqft'
  | 'property_type'
  | 'publication_date'
  // Read by the BO Republish action to warn when a restored listing would be
  // auto-expired the same night (ADR-059).
  | 'expiration_date'
  | 'created_at'
  | 'updated_at'
  | 'deleted_at'
>;

export type ListAdminDealsParams = {
  status?: AdminDealStatus | 'all';
  searchQuery?: string;
  /** Property-type whitelist (ADR-027). Empty / undefined = no restriction. */
  propertyType?: PropertyType[];
  pageSize?: number;
  offset?: number;
};

export type ListAdminDealsResult = {
  rows: AdminDealRow[];
  total: number;
};

const ADMIN_DEALS_PAGE_SIZE = 25;

const SELECT_ADMIN_DEAL_COLUMNS =
  'id, reference_id, title, building, type, status, zone, asking_price, discount_pct, bedrooms, area_sqft, property_type, publication_date, expiration_date, created_at, updated_at, deleted_at';

/**
 * Admin-side listing of deals: returns ALL statuses (draft / published /
 * sold / archived / expired) ordered by created_at desc. Distinct from
 * `searchPublishedDeals` which restricts to (published, sold) for the
 * public catalogue.
 *
 * RLS (`deals_admin_select` from Lot 3) authorizes
 * is_active_admin / is_active_super_admin to read every status; the front
 * does not guard further beyond optional UI-side status filtering.
 */
export async function listAdminDeals(
  params: ListAdminDealsParams = {},
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<ListAdminDealsResult> {
  const pageSize = params.pageSize ?? ADMIN_DEALS_PAGE_SIZE;
  const offset = params.offset ?? 0;

  let q = client
    .from('deals')
    .select(SELECT_ADMIN_DEAL_COLUMNS, { count: 'exact' })
    .is('deleted_at', null);

  if (params.status && params.status !== 'all') {
    q = q.eq('status', params.status);
  }

  if (params.propertyType && params.propertyType.length > 0) {
    q = q.in('property_type', params.propertyType);
  }

  if (params.searchQuery && params.searchQuery.trim().length > 0) {
    const term = escapeIlikeTerm(params.searchQuery.trim());
    if (term.length > 0) {
      q = q.or(`title.ilike.%${term}%,building.ilike.%${term}%,reference_id.ilike.%${term}%`);
    }
  }

  const { data, error, count } = await q
    .order('updated_at', { ascending: false })
    .range(offset, offset + pageSize - 1);

  if (error) throw error;
  return {
    rows: (data ?? []) as AdminDealRow[],
    total: count ?? 0,
  };
}
